// Observation record (/observation/:id) — the GTIS instrument, digitized (Module F).
//
// Lead + draft      → the full editable instrument: rating chips per indicator, per-section
//                     comments, discussion notes → Save · Finalize (after the 1-on-1 talk).
// Lead + finalized  → read-only record (+ acknowledged state).
// Teacher (owner)   → their own finalized record, read-only, with one calm Acknowledge action —
//                     the in-app signature line of the paper form.
//
// Tone rails: developmental, never a leaderboard. Totals are the school form's own arithmetic on
// this private record. Empty ratings read as "not yet rated", never as errors.

import { api } from "../api";
import { esc } from "../dom";
import { me } from "../state";
import { navigate } from "../router";
import { breadcrumbs } from "../components/nav";

interface Indicator { id: string; label: string }
interface Section { id: string; title: string; indicators: Indicator[]; max: number }
interface SectionTotal { section_id: string; title: string; points: number; max: number; rated: number }
interface ObsDetail {
  id: string;
  teacher_id: string;
  teacher_name: string;
  observer_name: string;
  kind: "classroom" | "marking";
  observed_on: string;
  class_label: string | null;
  topic: string | null;
  ratings: Record<string, number | string>;
  comments: Record<string, string>;
  meta: Record<string, unknown>;
  strengths: string | null;
  growth: string | null;
  next_steps: string | null;
  status: "draft" | "finalized" | "acknowledged";
  finalized_at: string | null;
  acknowledged_at: string | null;
  totals: { sections: SectionTotal[]; total: number; max: number } | null;
  instrument: { sections: Section[]; scale: (number | string)[]; legend: string };
}
interface ObsResponse {
  observation: ObsDetail;
  can_edit: boolean;
  can_finalize: boolean;
  can_acknowledge: boolean;
}

const KIND_LABEL = { classroom: "Classroom observation", marking: "Book & worksheet check" } as const;

export async function renderObservation(app: HTMLElement, params: Record<string, string>): Promise<void> {
  const id = params.id!;
  let resp = await api<ObsResponse>(`/api/observations/${encodeURIComponent(id)}`);
  // Local working state for the editable draft (saved on Save; chips update it immediately).
  let ratings: Record<string, number | string> = { ...resp.observation.ratings };
  let err: string | null = null;
  let flash = "";

  const isLead = me()?.auth_role === "lead";

  // ── pieces ────────────────────────────────────────────────────────────────────
  function statusLine(o: ObsDetail): string {
    if (o.status === "draft") return `<span class="chip">Draft — visible only to you until finalized</span>`;
    if (o.status === "finalized") return `<span class="chip submitted">Finalized</span>`;
    return `<span class="chip approved">Acknowledged ✓</span>`;
  }

  function ratingChips(ind: Indicator, editable: boolean, scale: (number | string)[]): string {
    const current = ratings[ind.id];
    const chips = scale
      .map((v) => {
        const on = current === v;
        return editable
          ? `<button type="button" class="rate-chip${on ? " on" : ""}" data-ind="${esc(ind.id)}" data-val="${esc(v)}">${esc(v)}</button>`
          : `<span class="rate-chip${on ? " on" : ""}" aria-hidden="true">${esc(v)}</span>`;
      })
      .join("");
    const unrated = current === undefined ? `<span class="muted" style="font-size:var(--text-sm)"> not yet rated</span>` : "";
    return `<div class="rate-row"><div class="rate-label">${esc(ind.label)}${editable ? "" : unrated}</div><div class="rate-chips">${chips}</div></div>`;
  }

  function sectionBlock(s: Section, o: ObsDetail, editable: boolean): string {
    const rows = s.indicators.map((i) => ratingChips(i, editable, o.instrument.scale)).join("");
    const tot = o.totals?.sections.find((t) => t.section_id === s.id);
    const totLine =
      tot && o.kind === "classroom"
        ? `<p class="muted" style="margin-top:var(--space-2)">Section: ${tot.points} / ${tot.max}${tot.rated < s.indicators.length ? ` · ${tot.rated} of ${s.indicators.length} rated` : ""}</p>`
        : "";
    const comment = editable
      ? `<label for="c-${esc(s.id)}" class="hint" style="margin-top:var(--space-2)">Section comments</label>
         <textarea id="c-${esc(s.id)}" data-section="${esc(s.id)}" class="obs-comment">${esc(o.comments[s.id] ?? "")}</textarea>`
      : o.comments[s.id]
        ? `<p style="margin-top:var(--space-2)"><strong>Comments:</strong> ${esc(o.comments[s.id])}</p>`
        : "";
    return `<div class="card"><h2 style="margin-top:0">${esc(s.title)}</h2>${rows}${totLine}${comment}</div>`;
  }

  function metaBlock(o: ObsDetail, editable: boolean): string {
    if (o.kind !== "marking") return "";
    const m = o.meta as { books?: number; worksheets?: number; enrolment?: number };
    if (!editable) {
      const parts = [
        m.books !== undefined ? `${m.books} exercise books` : "",
        m.worksheets !== undefined ? `${m.worksheets} worksheet booklets` : "",
        m.enrolment !== undefined ? `class enrolment ${m.enrolment}` : "",
      ].filter(Boolean);
      return parts.length ? `<div class="card"><strong>Submitted for checking</strong><p>${esc(parts.join(" · "))}</p></div>` : "";
    }
    return `<div class="card"><strong>Submitted for checking</strong>
      <label for="m-books">Exercise books submitted</label>
      <input id="m-books" type="number" min="0" value="${m.books ?? ""}" />
      <label for="m-worksheets">Worksheet booklets submitted</label>
      <input id="m-worksheets" type="number" min="0" value="${m.worksheets ?? ""}" />
      <label for="m-enrolment">Class enrolment</label>
      <input id="m-enrolment" type="number" min="0" value="${m.enrolment ?? ""}" /></div>`;
  }

  function notesBlock(o: ObsDetail, editable: boolean): string {
    if (editable) {
      return `<div class="card"><h2 style="margin-top:0">Discussion notes</h2>
        <p class="hint">Start with what's working, then talk growth — a 1-on-1 conversation, solutions not blame.</p>
        <label for="n-strengths">What's working</label>
        <textarea id="n-strengths">${esc(o.strengths ?? "")}</textarea>
        <label for="n-growth">Growth areas</label>
        <textarea id="n-growth">${esc(o.growth ?? "")}</textarea>
        <label for="n-next">Agreed next steps</label>
        <textarea id="n-next">${esc(o.next_steps ?? "")}</textarea></div>`;
    }
    if (!o.strengths && !o.growth && !o.next_steps) return "";
    const p = (label: string, v: string | null): string => (v ? `<p><strong>${label}:</strong> ${esc(v)}</p>` : "");
    return `<div class="card"><h2 style="margin-top:0">Discussion notes</h2>
      ${p("What's working", o.strengths)}${p("Growth areas", o.growth)}${p("Agreed next steps", o.next_steps)}</div>`;
  }

  function headerBlock(o: ObsDetail, editable: boolean): string {
    const when = editable
      ? `<label for="h-date">Date</label><input id="h-date" type="date" value="${esc(o.observed_on)}" />
         <label for="h-class">Class</label><input id="h-class" placeholder="G4A" value="${esc(o.class_label ?? "")}" />
         <label for="h-topic">${o.kind === "classroom" ? "Topic" : "Subject"}</label><input id="h-topic" value="${esc(o.topic ?? "")}" />`
      : `<p class="muted">${esc(o.observed_on)}${o.class_label ? ` · ${esc(o.class_label)}` : ""}${o.topic ? ` · ${esc(o.topic)}` : ""}</p>`;
    return `${when}`;
  }

  function totalBlock(o: ObsDetail): string {
    if (o.kind !== "classroom" || !o.totals) return "";
    const ratedAll = o.totals.sections.reduce((n, s) => n + s.rated, 0);
    if (ratedAll === 0) return "";
    return `<div class="card"><strong>Overall (the form's own total)</strong>
      ${o.totals.sections.map((s) => `<div class="cal-row"><span class="cal-label">${esc(s.title)}</span><span class="muted">${s.points} / ${s.max}</span></div>`).join("")}
      <div class="cal-row"><span class="cal-label"><strong>Total</strong></span><span><strong>${o.totals.total} / ${o.totals.max}</strong></span></div>
      <p class="hint" style="margin-top:var(--space-2)">Developmental — discussed 1-on-1, never team-visible.</p></div>`;
  }

  // The hint + Discard sit inline (above the sticky bar) — secondary, no need to pin them.
  function preActions(r: ObsResponse): string {
    if (!r.can_edit) return "";
    const o = r.observation;
    return `<p class="hint" style="margin-top:var(--space-4)">Finalize after the 1-on-1 conversation. ${esc(o.teacher_name)} will then see this record and can acknowledge it.</p>
      <button class="linkbtn quiet" id="discard" type="button">Discard draft</button>`;
  }

  // The primary action(s) live in a sticky bar (.obs-actions) so they're reachable without
  // scrolling the whole instrument — the form can run very long. One CTA for the teacher
  // (Acknowledge); Save + Finalize for the Lead.
  function barActions(r: ObsResponse): string {
    const o = r.observation;
    if (r.can_edit) {
      return `<button class="btn secondary" id="save" type="button">Save draft</button>
        <div style="height:8px"></div>
        <button class="btn moss" id="finalize" type="button">Finalize — discussed with ${esc(o.teacher_name)}</button>`;
    }
    if (r.can_acknowledge) {
      return `<button class="btn" id="ack" type="button">Acknowledge — I've reviewed and discussed this</button>`;
    }
    if (o.status === "acknowledged" && o.acknowledged_at) {
      return `<p class="muted" style="margin:0">Acknowledged ${esc(o.acknowledged_at)} ✓</p>`;
    }
    return "";
  }

  // ── render ────────────────────────────────────────────────────────────────────
  function render(): void {
    const o = resp.observation;
    const editable = resp.can_edit;
    const bar = barActions(resp);

    // Where this record sits: the Lead came from the teacher's page in Team; a teacher from their list.
    app.innerHTML = `
      <div class="topbar">
        ${breadcrumbs(isLead
          ? [{ label: "Team", href: "/team" }, { label: o.teacher_name, href: `/team/${encodeURIComponent(o.teacher_id)}` }, { label: KIND_LABEL[o.kind] }]
          : [{ label: "My observations", href: "/observations" }, { label: KIND_LABEL[o.kind] }])}
        <button class="pill-btn" id="print" type="button">Print</button>
      </div>
      <h1>${KIND_LABEL[o.kind]}</h1>
      <p class="muted">${esc(o.teacher_name)} · observed by ${esc(o.observer_name)}</p>
      <div style="margin:var(--space-2) 0">${statusLine(o)}</div>
      <p class="hint">A conversation about teaching, not an audit.</p>
      ${flash ? `<div class="toast">${esc(flash)}</div>` : ""}
      ${err ? `<p class="error">${esc(err)}</p>` : ""}
      <div id="obsroot">
        ${headerBlock(o, editable)}
        <p class="hint" style="margin-top:var(--space-3)">${esc(o.instrument.legend)}</p>
        ${o.instrument.sections.map((s) => sectionBlock(s, o, editable)).join("")}
        ${metaBlock(o, editable)}
        ${totalBlock(o)}
        ${notesBlock(o, editable)}
        ${preActions(resp)}
        ${bar ? `<div class="obs-actions">${bar}</div>` : ""}
      </div>`;
    flash = "";
    wire();
  }

  // ── interactions ─────────────────────────────────────────────────────────────
  function collectPatch(): Record<string, unknown> {
    const o = resp.observation;
    const val = (sel: string): string => (app.querySelector(sel) as HTMLInputElement | HTMLTextAreaElement | null)?.value ?? "";
    const comments: Record<string, string> = {};
    app.querySelectorAll<HTMLTextAreaElement>(".obs-comment").forEach((t) => {
      const sid = t.dataset.section!;
      if (t.value.trim()) comments[sid] = t.value.trim();
    });
    const patch: Record<string, unknown> = {
      observed_on: val("#h-date") || o.observed_on,
      class_label: val("#h-class"),
      topic: val("#h-topic"),
      ratings,
      comments,
      strengths: val("#n-strengths"),
      growth: val("#n-growth"),
      next_steps: val("#n-next"),
    };
    if (o.kind === "marking") {
      const num = (sel: string): number | undefined => {
        const v = val(sel);
        return v === "" ? undefined : Number(v);
      };
      const meta: Record<string, number> = {};
      const books = num("#m-books");
      const worksheets = num("#m-worksheets");
      const enrolment = num("#m-enrolment");
      if (books !== undefined) meta.books = books;
      if (worksheets !== undefined) meta.worksheets = worksheets;
      if (enrolment !== undefined) meta.enrolment = enrolment;
      patch.meta = meta;
    }
    return patch;
  }

  async function mutate(fn: () => Promise<void>): Promise<void> {
    err = null;
    try {
      await fn();
      resp = await api<ObsResponse>(`/api/observations/${encodeURIComponent(id)}`);
      ratings = { ...resp.observation.ratings };
    } catch (e) {
      err = (e as Error)?.message ?? "Something went wrong.";
    }
    render();
  }

  function wire(): void {
    // Print → a clean paper artifact for the 1-on-1 (the @media print block strips the chrome).
    app.querySelector<HTMLButtonElement>("#print")?.addEventListener("click", () => window.print());

    // Rating chips: tap to set; tap the same value again to clear (back to not-yet-rated).
    app.querySelectorAll<HTMLButtonElement>("button.rate-chip").forEach((b) => {
      b.addEventListener("click", () => {
        const ind = b.dataset.ind!;
        const raw = b.dataset.val!;
        const v: number | string = resp.observation.kind === "classroom" ? Number(raw) : raw;
        if (ratings[ind] === v) delete ratings[ind];
        else ratings[ind] = v;
        // Quiet visual update without a full re-render (keeps scroll position under the thumb).
        const row = b.closest(".rate-row")!;
        row.querySelectorAll(".rate-chip").forEach((c) => c.classList.remove("on"));
        if (ratings[ind] !== undefined) b.classList.add("on");
      });
    });

    app.querySelector("#save")?.addEventListener("click", () =>
      mutate(async () => {
        await api(`/api/observations/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(collectPatch()) });
        flash = "Draft saved.";
      }),
    );

    app.querySelector("#finalize")?.addEventListener("click", () =>
      mutate(async () => {
        // Save first so what's finalized is exactly what's on screen.
        await api(`/api/observations/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(collectPatch()) });
        await api(`/api/observations/${encodeURIComponent(id)}/finalize`, { method: "POST" });
        flash = "Finalized.";
      }),
    );

    const discard = app.querySelector<HTMLButtonElement>("#discard");
    discard?.addEventListener("click", async () => {
      if (discard.dataset.confirm !== "1") {
        discard.dataset.confirm = "1";
        discard.textContent = "Discard? Tap again";
        return;
      }
      try {
        await api(`/api/observations/${encodeURIComponent(id)}`, { method: "DELETE" });
        navigate(isLead ? `/team/${encodeURIComponent(resp.observation.teacher_id)}` : "/observations");
      } catch (e) {
        err = (e as Error)?.message ?? "Something went wrong.";
        render();
      }
    });

    app.querySelector("#ack")?.addEventListener("click", () =>
      mutate(async () => {
        await api(`/api/observations/${encodeURIComponent(id)}/acknowledge`, { method: "POST" });
        flash = "Thank you — acknowledged.";
      }),
    );
  }

  render();
}
